/**
 * Converts the structured `/api/search/execute` parse result into the same `string | {pill}`
 * segment shape templates.js uses -- `resetToSegments` then resolves each pill against the live
 * term index into a real, editable token, exactly like a clicked template.
 *
 * A *continuous* metric's custom/preset bands aren't reconstructable this way (the parsed shape
 * carries no band data), so a re-tokenized "Local Voter Score of 41-60, 61-80" comes back as just
 * "Local Voter Score by {Aggregation}" -- its band choice is genuinely lost (the backend's
 * `ContinuousMetricChoice` schema has no field for it), but its `aggregation` is always present
 * and IS preserved, composed the same "{display} by {Label}" way `BandSelector` itself shows for
 * an unbanded continuous token -- the exact text `resetToSegments` recognizes and parses back
 * into a real `bandSelection` (`{isAll: true, aggregation}`). Real bug: a Data Catalog selection
 * of "Average Local Voter Score" (continuous, `aggregation: "avg"`) used to land as a bare
 * "Local Voter Score" pill, silently reverting to whatever the default aggregation happened to be
 * on next search. Age is the other exception: it's a *categorical* metric that DOES carry its
 * selected bands in `display_values`, composed here as "Ages {bands}" -- the exact text
 * `resetToSegments` recognizes as Age's own format and parses back into a real `bandSelection`.
 *
 * Connector words ("among", "voters", "in") match the one confirmed example:
 * `{metrics} among {filters} voters in {places}` -- a best-effort default, not confirmed for
 * every combination.
 *
 * When the parse carries `source_spans` (the words of the typed query each item came from), the
 * segments instead follow the typed query: each item's pills replace its own words and everything
 * no parser claimed stays as plain text in place. Items with no span lead. The implicit topline
 * (see topline.js) is never a pill, in either shape.
 */
import { combinePlaceParts } from "./combinePlaceParts";
import { formatAggregationLabel } from "./formatAggregationLabel";
import { isImplicitTopline } from "./topline";

const metricPills = (metric) => {
  if (metric.metric === "age" && metric.display_values?.length > 0) {
    return [{ pill: `Ages ${metric.display_values.join(", ")}` }];
  }
  const isAllSentinel = metric.value?.length === 1 && metric.value[0] === "all";
  if (metric.type === "categorical" && !isAllSentinel && metric.display_values?.length > 0) {
    return metric.display_values.map((value) => ({ pill: `${metric.display} ${value}` }));
  }
  if (metric.type === "continuous" && metric.aggregation) {
    return [{ pill: `${metric.display} by ${formatAggregationLabel(metric.aggregation)}` }];
  }
  return [{ pill: metric.display }];
};

const filterPills = (filter) => (filter.display_values || []).map((value) => ({ pill: value }));

// Multiple places drilled into the same geography level each carry their own self-contained
// "{level} in {place}" phrase -- see combinePlaceParts.js for de-duplicating that.
const placePills = (places) => combinePlaceParts(places).map((name) => ({ pill: name }));

// Interleaves a bare space between consecutive pills of the same group -- resetToSegments
// merges adjacent plain-string pieces on its own, so this doesn't need to be smarter than that.
const withSpaces = (pills) => pills.flatMap((pill, i) => (i === 0 ? [pill] : [" ", pill]));

const spanKey = (span) => `${span.start}:${span.end}`;

// Places quoting the same words (every state of "pacific coast states") are one item, so
// combinePlaceParts can still merge them.
const placeItems = (places, placeSpans) =>
  Object.values(
    places.reduce((groups, place, i) => {
      const span = placeSpans?.[i] ?? null;
      const key = span ? spanKey(span) : `unspanned:${i}`;
      return { ...groups, [key]: { span, places: [...(groups[key]?.places ?? []), place] } };
    }, {})
  ).map(({ span, places: grouped }) => ({ span, pills: placePills(grouped) }));

// Dropping the topline's words can leave the text that followed them leading the bar.
const trimLeading = ([first, ...rest]) =>
  typeof first === "string" ? (first.trimStart() ? [first.trimStart(), ...rest] : rest) : [first, ...rest];

const segmentsFromSpans = (parameters, metrics, filters, places) => {
  const { query, metrics: metricSpans, filters: filterSpans, places: placeSpans } =
    parameters.source_spans;
  const items = [
    // The implicit topline keeps its span with no pills, so the words typed for it are dropped
    // from the bar instead of lingering as plain text.
    ...metrics.map((metric, i) => ({
      span: metricSpans?.[i] ?? null,
      pills: isImplicitTopline(metric) ? [] : metricPills(metric),
    })),
    ...filters.map((filter, i) => ({ span: filterSpans?.[i] ?? null, pills: filterPills(filter) })),
    ...placeItems(places, placeSpans),
  ];
  const leading = withSpaces(items.filter((item) => !item.span).flatMap((item) => item.pills));
  const spanned = Object.values(
    items
      .filter((item) => item.span)
      .reduce((groups, { span, pills }) => {
        const key = spanKey(span);
        return { ...groups, [key]: { span, pills: [...(groups[key]?.pills ?? []), ...pills] } };
      }, {})
  ).sort((a, b) => a.span.start - b.span.start);

  const { segments, cursor } = spanned.reduce(
    (acc, { span, pills }) => {
      const gap = query.slice(acc.cursor, span.start);
      return {
        segments: [...acc.segments, ...(gap ? [gap] : []), ...withSpaces(pills)],
        cursor: span.end,
      };
    },
    { segments: leading.length > 0 ? [...leading, " "] : [], cursor: 0 }
  );
  const tail = query.slice(cursor);
  return trimLeading(tail ? [...segments, tail] : segments);
};

const hasValidSpans = (parameters) =>
  typeof parameters.source_spans?.query === "string" &&
  ["metrics", "filters", "places"].some((kind) => parameters.source_spans[kind]?.some(Boolean));

export function parametersToSegments(parameters) {
  if (!parameters) return [];

  const allMetrics = parameters.metrics?.metrics || [];
  const filters = parameters.filters?.filters || [];
  const places = parameters.places || [];

  if (hasValidSpans(parameters)) return segmentsFromSpans(parameters, allMetrics, filters, places);

  // The LLM parser doesn't consistently classify a categorical selection as a filter vs. a
  // metric (e.g. "ethnicity" lands as either) -- any categorical metric that isn't the "all"
  // sentinel (topline's `value: ["all"]`, which carries no real information) expands into one
  // pill per value here, each prefixed with the metric's own display name, exactly matching
  // featureTypes.js's `categorical.renderBadge` (`${item.display} ${item.display_values[idx]}`)
  // -- a bare value like "21-40" or "Voted" is meaningless without its parent metric (Turnout
  // Score Bucket, Vote History) attached, unlike Age, which keeps its own composed
  // "Ages {bands}" format that `resetToSegments` specifically recognizes.
  const metricPillList = allMetrics.filter((metric) => !isImplicitTopline(metric)).flatMap(metricPills);
  const filterPillList = filters.flatMap(filterPills);
  const placePillList = placePills(places);

  const segments = [...withSpaces(metricPillList)];

  if (filterPillList.length > 0) {
    if (segments.length > 0) segments.push(" among ");
    segments.push(...withSpaces(filterPillList));
    segments.push(" voters");
  }

  if (placePillList.length > 0) {
    if (segments.length > 0) segments.push(" in ");
    segments.push(...withSpaces(placePillList));
  }

  return segments;
}
